import { useContext, useMemo } from 'react'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlus } from '@fortawesome/pro-light-svg-icons'

import { Button, DashboardModule } from '@domain-group/suit-theme'

import {
  digObject, matchFilterString, sortArrayBy, toggleArray,
} from '@domain-group/javascript-utils'

import { useFilters } from '@domain-group/react-hooks'

import PageContext from '@contexts/pageContext'

import useSystem from '@hooks/useSystem'

import DashboardModuleFilters from '@components/DashboardModuleFilters'

import type { DataStoreItemKeyModel } from '@models/types'

import { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import DataStoreItemKeyListItem from './DataStoreItemKeyListItem'

const buildPageFilterFields = () => {
  const style = {
    marginBottom: 16,
  }

  return [
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        style,
      },
      key: 'string',
      label: 'Filter by Title',
    },
  ]
}

type UseSystemPayload = ReturnType<typeof useSystem>

type DeleteItemKeyParams = {
  dataStoreItemKey: DataStoreItemKeyModel,
  dataStoreItemKeys: DataStoreItemKeyModel[],
  options: object,
  updateFn: UseSystemPayload['callbacks']['updateSystem'],
  updateSystem: (payload: HandleCallbackActionParams) => Promise<object>,
}

const deleteItemKey = (params: DeleteItemKeyParams) => {
  const {
    dataStoreItemKey, dataStoreItemKeys, options, updateFn, updateSystem,
  } = params

  const updatedDataStoreItemKeys = toggleArray(dataStoreItemKeys, dataStoreItemKey, { deepCompare: true })

  const updatedSystem = {
    options: {
      ...options,
      data_store_item_keys: updatedDataStoreItemKeys,
    },
  }

  const payload = {
    callbacks: {
      action: updateFn,
    },
    entityParams: updatedSystem,
    toastText: 'Data Store Item Key Deleted',
  }

  updateSystem(payload)
}

const DataStoreItemKeys = (props: { systemPayload: UseSystemPayload }) => {
  const { systemPayload } = props

  const {
    callbacks: {
      createOrEditDataStoreItemKey,
      updateSystem: updateFn,
    },
    system: {
      cache_key,
      id,
      options,
    },
  } = systemPayload

  const pageContext = useContext(PageContext)
  const {
    callbacks: {
      updateSystem,
    },
  } = pageContext

  const filterPayload = useFilters()
  // @ts-expect-error TS2339
  const { pageFilters: { string } } = filterPayload

  const dataStoreItemKeys: DataStoreItemKeyModel[] = digObject(options, 'data_store_item_keys', [])
  const hasKeys = !!dataStoreItemKeys.length

  const filteredDataStoreItemKeys = useMemo(() => {
    const filtered = dataStoreItemKeys.filter(({ title }) => matchFilterString(title, string))
    return sortArrayBy(filtered, 'asc', 'title')
  }, [cache_key, id, string])

  return (
    <DashboardModule
      contentBoxProps={{ flexDirection: 'column' }}
      headerRight={<DashboardModule.FilterHeader {...filterPayload} />}
      title="Data Store Item Keys"
    >
      <DashboardModuleFilters
        filterFields={buildPageFilterFields()}
        {...filterPayload}
      />

      {!hasKeys && (
        <DashboardModule.BlankState>
          <DashboardModule.BlankState.Title>
            No Data Store Item Keys present.
          </DashboardModule.BlankState.Title>

          <DashboardModule.BlankState.Paragraph>
            Please adjust your filters or add a Data Store Item Key.
          </DashboardModule.BlankState.Paragraph>
        </DashboardModule.BlankState>
      )}

      {hasKeys && (
        // @ts-expect-error TS2322
        <DashboardModule.ScrollableSection flexDirection="column">
          {filteredDataStoreItemKeys.map((dataStoreItemKey: DataStoreItemKeyModel) => (
            <DataStoreItemKeyListItem
              callbacks={{
                deleteDataStoreItemKey: () => deleteItemKey({
                  dataStoreItemKey, dataStoreItemKeys, options, updateFn, updateSystem,
                }),
                createOrEditDataStoreItemKey,
              }}
              key={dataStoreItemKey.id}
              dataStoreItemKey={dataStoreItemKey}
            />
          ))}
        </DashboardModule.ScrollableSection>
      )}

      <DashboardModule.Footer>
        <Button
          buttonStyle="secondaryEdit"
          icon={<FontAwesomeIcon icon={faPlus} />}
          marginLeft="auto"
          onClick={() => createOrEditDataStoreItemKey()}
          // @ts-expect-error TS2322
          size={['medium', 'small']}
          width={['100%', 'fit-content']}
        >
          Add Data Store Item
        </Button>
      </DashboardModule.Footer>
    </DashboardModule>
  )
}

export default DataStoreItemKeys
